import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Plus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { customerApi, orgApi } from "@/lib/entities";
import { CustomerFormDialog } from "@/pages/customers/CustomerFormDialog";
import { ContactFormDialog } from "@/pages/contacts/ContactFormDialog";
import { TourFormDialog } from "@/pages/tours/TourFormDialog";

const WRITE_ROLES_DEPRECATED = ["super_admin", "leasing_sales", "ops_dispatch"]; // Wave 4B-5 P1: superseded by can(); retained only for reference; not used.

/**
 * Compact "+" quick-add menu.
 * Context-aware:
 *   - On /customers/:id → "New Contact" and "New Tour" preselect that customer.
 *   - On /tours/:id → placeholder items that link users to the existing
 *     assignment flows.  (Assignment modals live inside TourDetail so we
 *     surface them via anchor navigation with a hash the page can pick up.)
 */
export const QuickAdd = ({ compact = false, onOpenCustomer, onOpenContact, onOpenTour }) => {
  const { user, can } = useAuth();
  const loc = useLocation();
  const [openWhat, setOpenWhat] = useState(null); // 'customer' | 'contact' | 'tour'
  const [customers, setCustomers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [ctxCustomer, setCtxCustomer] = useState(null);

  // Detect route context
  const customerMatch = loc.pathname.match(/^\/customers\/([^/]+)/);
  const tourMatch = loc.pathname.match(/^\/tours\/([^/]+)/);
  const activeCustomerId = customerMatch?.[1];
  const activeTourId = tourMatch?.[1];

  // Wave 4B-5 P1: capability-based (backend perm() is authoritative).
  // Trigger shows if the user can create AT LEAST ONE of the menu items.
  const canCustomer = can("customers.create");
  const canContact = can("contacts.create");
  const canTour = can("tours.create");
  const canCoach = can("assets.create");
  const canDriver = can("drivers.create");
  const canWrite = user && (canCustomer || canContact || canTour || canCoach || canDriver);

  const handleOpen = (what) => {
    if (what === "customer" && onOpenCustomer) return onOpenCustomer();
    if (what === "contact" && onOpenContact) return onOpenContact();
    if (what === "tour" && onOpenTour) return onOpenTour();
    setOpenWhat(what);
  };

  // Lazy-load lists on open
  useEffect(() => {
    if (!openWhat) return;
    if (openWhat === "tour" && customers.length === 0) {
      customerApi
        .list({ active: true, page_size: 200 })
        .then((d) => setCustomers(d.results || []))
        .catch(() => setCustomers([]));
    }
    if ((openWhat === "customer" || openWhat === "contact") && orgs.length === 0) {
      orgApi
        .list({ page_size: 200 })
        .then((d) => setOrgs(d.results || []))
        .catch(() => setOrgs([]));
    }
    if (openWhat === "contact" && activeCustomerId && !ctxCustomer) {
      customerApi
        .get(activeCustomerId)
        .then(setCtxCustomer)
        .catch(() => setCtxCustomer(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openWhat, activeCustomerId]);

  if (!canWrite) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid="quick-add-trigger"
            title="Quick Add"
            className={
              compact
                ? "inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-white/10"
                : "inline-flex items-center gap-1 px-2 h-8 rounded-md hover:bg-white/10 text-[13px]"
            }
            style={{ color: "var(--fg-primary)" }}
          >
            <Plus size={16} />
            {!compact && <span>New</span>}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-[11px] uppercase tracking-widest text-[color:var(--fg-muted)]">
            Quick Add
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {canCustomer && (
          <DropdownMenuItem
            data-testid="quick-add-client"
            onClick={() => handleOpen("customer")}
          >
            New Client
          </DropdownMenuItem>
          )}
          {canContact && (
          <DropdownMenuItem
            data-testid="quick-add-contact"
            onClick={() => handleOpen("contact")}
          >
            New Contact
            {activeCustomerId && (
              <span className="ml-auto text-[10px] text-coach-blue">
                → this client
              </span>
            )}
          </DropdownMenuItem>
          )}
          {canTour && (
          <DropdownMenuItem
            data-testid="quick-add-tour"
            onClick={() => handleOpen("tour")}
          >
            New Tour
            {activeCustomerId && (
              <span className="ml-auto text-[10px] text-coach-blue">
                → this client
              </span>
            )}
          </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          {canCoach && (
          <DropdownMenuItem asChild>
            <a data-testid="quick-add-coach" href="/fleet">New Coach</a>
          </DropdownMenuItem>
          )}
          {canDriver && (
          <DropdownMenuItem asChild>
            <a data-testid="quick-add-driver" href="/drivers">New Driver</a>
          </DropdownMenuItem>
          )}
          {activeTourId && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[11px] uppercase tracking-widest text-[color:var(--fg-muted)]">
                On This Tour
              </DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <a
                  data-testid="quick-add-tour-coach"
                  href={`/tours/${activeTourId}#coaches`}
                >
                  Assign Coach
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a
                  data-testid="quick-add-tour-driver"
                  href={`/tours/${activeTourId}#drivers`}
                >
                  Assign Driver
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a
                  data-testid="quick-add-tour-contact"
                  href={`/tours/${activeTourId}#contacts`}
                >
                  Add Tour Contact
                </a>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {openWhat === "customer" && (
        <CustomerFormDialog
          open
          orgs={orgs}
          onClose={() => setOpenWhat(null)}
          onSaved={(cust) => {
            setOpenWhat(null);
            if (cust?.id) window.location.href = `/customers/${cust.id}`;
          }}
        />
      )}
      {openWhat === "contact" && (
        <ContactFormDialog
          open
          orgs={orgs}
          defaultCustomer={activeCustomerId ? ctxCustomer : null}
          onClose={() => setOpenWhat(null)}
          onSaved={() => setOpenWhat(null)}
        />
      )}
      {openWhat === "tour" && (
        <TourFormDialog
          open
          customers={customers}
          initial={activeCustomerId ? { customer_id: activeCustomerId, status: "inquiry" } : null}
          lockCustomer={!!activeCustomerId}
          onClose={() => setOpenWhat(null)}
          onSaved={() => setOpenWhat(null)}
        />
      )}
    </>
  );
};
